/**
 * Asset-creation file picker (Jonathan 2026-05-21).
 *
 * A fake "system file picker" styled as a Windows-95 dialog (silver bevelled
 * chrome, navy title bar, sunken white list with navy selection, dark text)
 * over the fictional drive `C:`. Its folders/files are enumerated at build
 * time from `src/drive/` (see `AssetService`); picking one "uploads" the real
 * image as the player's created asset. A permanent footer carries three
 * buttons — Take Photo (simulate a live camera shot into the Camera Roll),
 * Cancel, and Select — centred as a group.
 *
 * Entered from the pause menu (`returnTo: "runner"`), the launcher
 * (`returnTo: "launcher"`) or the profile editor (`returnTo: "profile"`),
 * Variant B only. Controller-first: ↑/↓ navigate, A selects, X takes a photo,
 * Esc/B goes up or cancels. Mouse: the rows and footer buttons are clickable.
 */

import Phaser from "phaser";

import { NATIVE_WIDTH, PALETTE, UGC_CYAN } from "../config";
import { t } from "../i18n";

import { AssetService, type FakeFile } from "../services/AssetService";
import { InputService } from "../services/InputService";
import { Win95Button } from "../ui/Win95Button";
import { drawRaisedBevel, drawSunkenBevel } from "../ui/win95";

export interface FilePickerSceneInit {
  returnTo: "runner" | "launcher" | "profile";
}

/** Registry flag set when a pick was confirmed for the profile avatar, so the
 *  resumed `ProfileScene` knows to switch to the custom-asset avatar. */
export const PROFILE_AVATAR_PICKED_KEY = "profile.avatarPicked";

type Entry =
  | { kind: "folder"; name: string }
  | { kind: "up" }
  | { kind: "file"; folder: string; file: FakeFile };

const VISIBLE_ROWS = 6;
const ROW_H = 13;

// Compact Win95 dialog geometry (native render is 384×216). No title bar —
// just the silver frame holding the file list, preview and footer buttons.
// The dialog floats over the (paused) scene behind it on a transparent camera.
const WIN = { x: 47, y: 48, w: 290, h: 120 } as const;
const LIST = { x: 55, y: 56, w: 148, h: 80 } as const;
const PREVIEW = { x: 209, y: 56, w: 120, h: 80 } as const;
const BTN_W = 78;
const BTN_GAP = 8;
const BTN_CY = 151;

export class FilePickerScene extends Phaser.Scene {
  private returnTo: FilePickerSceneInit["returnTo"] = "launcher";
  private input2!: InputService;
  private telemetry!: {
    emit: (l: 1 | 2 | 3, t: string, d?: Record<string, number | string | boolean>) => void;
  };

  private currentFolder: string | null = null;
  private entries: Entry[] = [];
  private focusIdx = 0;
  private scrollOff = 0;
  private prevMoveY = 0;
  private prevMoveX = 0;
  private done = false;

  private rowTexts: Phaser.GameObjects.Text[] = [];
  private rowBgs: Phaser.GameObjects.Rectangle[] = [];
  private previewImg?: Phaser.GameObjects.Image;
  private previewCorner?: Phaser.GameObjects.Rectangle;
  private openedAtMs = 0;

  constructor() {
    super({ key: "FilePickerScene" });
  }

  init(data: FilePickerSceneInit): void {
    this.returnTo = data.returnTo ?? "launcher";
  }

  create(): void {
    this.input2 = new InputService(this);
    this.openedAtMs = this.time.now;

    // Phaser reuses the scene instance across launch/start, so per-open state
    // must be reset here — class-field initializers only run once, at
    // construction. Without this, a second open keeps `done = true` (frozen
    // input) and re-pushes row slots over destroyed objects (repaint crash).
    this.currentFolder = null;
    this.entries = [];
    this.focusIdx = 0;
    this.scrollOff = 0;
    this.prevMoveY = 0;
    this.prevMoveX = 0;
    this.done = false;
    this.rowTexts = [];
    this.rowBgs = [];
    this.previewImg = undefined;
    this.previewCorner = undefined;

    this.telemetry =
      (this.registry.get("telemetryService") as FilePickerScene["telemetry"] | undefined) ??
      { emit: () => undefined };

    // Transparent camera: the dialog floats over the paused scene behind it
    // (the profile editor / game) rather than painting its own background.
    this.cameras.main.setBackgroundColor("rgba(0,0,0,0)");

    // Soft drop shadow so the floating frame separates from whatever's behind.
    this.add.rectangle(WIN.x + 4, WIN.y + 4, WIN.w, WIN.h, 0x000000, 0.3).setOrigin(0, 0);

    // Window chrome: a raised silver frame + two sunken white fields. No title
    // bar — drawn into one Graphics object since none of it moves.
    const chrome = this.add.graphics();
    drawRaisedBevel(chrome, WIN.x, WIN.y, WIN.w, WIN.h);
    drawSunkenBevel(chrome, LIST.x, LIST.y, LIST.w, LIST.h);
    drawSunkenBevel(chrome, PREVIEW.x, PREVIEW.y, PREVIEW.w, PREVIEW.h);

    // List rows (slots painted by repaint()).
    for (let i = 0; i < VISIBLE_ROWS; i++) {
      const y = LIST.y + 1 + i * ROW_H;
      const bg = this.add
        .rectangle(LIST.x + 1, y, LIST.w - 2, ROW_H, PALETTE.win95Selection, 0)
        .setOrigin(0, 0);
      const txt = this.add
        .text(LIST.x + 6, y + ROW_H / 2, "", {
          fontFamily: "monospace",
          fontSize: "9px",
          color: this.hex(PALETTE.win95Text),
        })
        .setOrigin(0, 0.5);
      this.rowBgs.push(bg);
      this.rowTexts.push(txt);
    }

    // Footer: three buttons, centred as a group.
    const groupLeft = NATIVE_WIDTH / 2 - (3 * BTN_W + 2 * BTN_GAP) / 2;
    const cx = (i: number) => groupLeft + BTN_W / 2 + i * (BTN_W + BTN_GAP);
    new Win95Button(this, cx(0), BTN_CY, t("asset.btn.camera"), () => this.takePhoto(), BTN_W, Win95Button.HEIGHT, false, {
      width: 11,
      draw: (g, ix, iy) => FilePickerScene.drawCameraIcon(g, ix, iy),
    });
    new Win95Button(this, cx(1), BTN_CY, t("asset.btn.cancel"), () => this.cancel(), BTN_W);
    new Win95Button(this, cx(2), BTN_CY, t("asset.btn.select"), () => this.activate(), BTN_W, Win95Button.HEIGHT, true);

    this.rebuild();
    this.telemetry.emit(1, "ugc.asset.picker.open", { source: this.returnTo });
  }

  override update(): void {
    if (this.done) return;
    const s = this.input2.read();

    if (s.escPressed) {
      this.back();
      return;
    }
    if (s.attackPressed) {
      // X / J — the always-available camera action.
      this.takePhoto();
      return;
    }
    // A / D (and ←/→) walk the folder hierarchy: right descends into the
    // focused folder, left ascends to the parent.
    const dx = s.moveX > 0.5 ? 1 : s.moveX < -0.5 ? -1 : 0;
    if (dx !== this.prevMoveX) {
      this.prevMoveX = dx;
      if (dx > 0) this.descend();
      else if (dx < 0) this.ascend();
    }
    const dy = s.moveY > 0.5 ? 1 : s.moveY < -0.5 ? -1 : 0;
    if (dy !== this.prevMoveY) {
      this.prevMoveY = dy;
      if (dy !== 0) this.moveFocus(dy);
    }
    if (s.jumpPressed || s.interactPressed) {
      // A button / Space / Y / K — select / open the focused row.
      this.activate();
    }
  }

  /** Descend into the focused folder (D / →). No-op on files and ".. (up)". */
  private descend(): void {
    const entry = this.entries[this.focusIdx];
    if (entry?.kind !== "folder") return;
    this.currentFolder = entry.name;
    this.focusIdx = 0;
    this.scrollOff = 0;
    this.telemetry.emit(1, "ugc.asset.picker.open_folder", { folder: entry.name });
    this.rebuild();
  }

  /** Ascend to the parent folder (A / ←). No-op already at the drive root. */
  private ascend(): void {
    if (this.currentFolder === null) return;
    this.currentFolder = null;
    this.focusIdx = 0;
    this.scrollOff = 0;
    this.rebuild();
  }

  // -- list -----------------------------------------------------------------

  private computeEntries(): Entry[] {
    if (this.currentFolder === null) {
      return AssetService.folders().map((f) => ({ kind: "folder", name: f.name }) as Entry);
    }
    const folder = AssetService.folders().find((f) => f.name === this.currentFolder);
    const list: Entry[] = [{ kind: "up" }];
    for (const file of folder?.files ?? []) {
      list.push({ kind: "file", folder: this.currentFolder, file });
    }
    return list;
  }

  private rebuild(): void {
    this.entries = this.computeEntries();
    this.focusIdx = Phaser.Math.Clamp(this.focusIdx, 0, this.entries.length - 1);
    this.scrollOff = Phaser.Math.Clamp(this.scrollOff, 0, Math.max(0, this.entries.length - VISIBLE_ROWS));
    this.repaint();
  }

  private moveFocus(dir: number): void {
    this.focusIdx = Phaser.Math.Clamp(this.focusIdx + dir, 0, this.entries.length - 1);
    if (this.focusIdx < this.scrollOff) this.scrollOff = this.focusIdx;
    if (this.focusIdx >= this.scrollOff + VISIBLE_ROWS) this.scrollOff = this.focusIdx - VISIBLE_ROWS + 1;
    this.repaint();
  }

  private repaint(): void {
    for (let i = 0; i < VISIBLE_ROWS; i++) {
      const entryIdx = this.scrollOff + i;
      const entry = this.entries[entryIdx];
      const bg = this.rowBgs[i]!;
      const txt = this.rowTexts[i]!;
      if (!entry) {
        txt.setText("");
        bg.setFillStyle(PALETTE.win95Selection, 0);
        continue;
      }
      txt.setText(this.entryLabel(entry));
      const focused = entryIdx === this.focusIdx;
      bg.setFillStyle(PALETTE.win95Selection, focused ? 1 : 0);
      txt.setColor(this.hex(focused ? PALETTE.win95SelectionText : PALETTE.win95Text));
    }
    this.updatePreview();
  }

  private entryLabel(entry: Entry): string {
    switch (entry.kind) {
      case "folder":
        return `[+] ${entry.name}`;
      case "up":
        return t("asset.up");
      case "file":
        return `    ${entry.file.name}`;
    }
  }

  private updatePreview(): void {
    this.previewImg?.destroy();
    this.previewImg = undefined;
    this.previewCorner?.destroy();
    this.previewCorner = undefined;
    const entry = this.entries[this.focusIdx];
    if (entry?.kind === "file") {
      const camera = entry.folder === "Camera Roll";
      const tw = PREVIEW.w - 16;
      const th = PREVIEW.h - 8;
      const key = AssetService.fileThumbnailKey(this, entry.file, camera, tw, th);
      const px = PREVIEW.x + PREVIEW.w / 2;
      const py = PREVIEW.y + PREVIEW.h / 2;
      this.previewImg = this.add.image(px, py, key).setOrigin(0.5, 0.5).setDepth(2);
      // Cyan UGC corner-mark on the preview image's top-left.
      this.previewCorner = this.add
        .rectangle(px - tw / 2, py - th / 2, 4, 4, UGC_CYAN, 1)
        .setOrigin(0, 0)
        .setDepth(3);
    }
  }

  // -- actions --------------------------------------------------------------

  private activate(): void {
    const entry = this.entries[this.focusIdx];
    if (!entry || this.done) return;
    switch (entry.kind) {
      case "folder":
        this.descend();
        break;
      case "up":
        this.ascend();
        break;
      case "file":
        this.select(entry.folder, entry.file);
        break;
    }
  }

  /** Take a "simulated live" photo and drop into the Camera Roll, focused on
   *  the freshly captured shot ready to Select. Always available from the
   *  footer button (or X / J). */
  private takePhoto(): void {
    if (this.done) return;
    const file = AssetService.capture();
    this.telemetry.emit(1, "ugc.asset.camera.capture", { name: file.name });
    this.currentFolder = "Camera Roll";
    this.scrollOff = 0;
    this.rebuild();
    // Newest capture is the first file, i.e. the row right after ".. (up)".
    this.focusIdx = 1;
    this.scrollOff = 0;
    this.repaint();
  }

  private select(folder: string, file: FakeFile): void {
    if (this.done) return;
    this.done = true;
    const source = folder === "Camera Roll" ? "camera" : "drive";
    AssetService.setUploaded({ source, folder, name: file.name, seed: file.seed, url: file.url });
    if (this.returnTo === "profile") this.registry.set(PROFILE_AVATAR_PICKED_KEY, true);
    this.telemetry.emit(2, "ugc.asset.upload", { source, name: file.name, folder });

    const cx = NATIVE_WIDTH / 2;
    const cy = 92;
    this.add
      .rectangle(cx, cy, 150, 26, PALETTE.win95Selection, 1)
      .setStrokeStyle(1, PALETTE.win95DarkShadow)
      .setDepth(50);
    this.add
      .text(cx, cy, t("asset.uploaded"), {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.hex(PALETTE.win95SelectionText),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5)
      .setDepth(51);
    this.time.delayedCall(600, () => this.returnToCaller());
  }

  /** Esc / B: step up out of a folder, or cancel the whole picker at root. */
  private back(): void {
    if (this.currentFolder !== null) {
      this.currentFolder = null;
      this.focusIdx = 0;
      this.scrollOff = 0;
      this.rebuild();
      return;
    }
    this.cancel();
  }

  /** Close the picker without uploading (footer Cancel / title close box). */
  private cancel(): void {
    if (this.done) return;
    this.telemetry.emit(1, "ugc.asset.picker.cancel", { dwellMs: Math.round(this.time.now - this.openedAtMs) });
    this.done = true;
    this.returnToCaller();
  }

  private returnToCaller(): void {
    if (this.returnTo === "runner") {
      this.scene.stop();
      this.scene.resume("runner");
    } else if (this.returnTo === "profile") {
      this.scene.stop();
      this.scene.resume("ProfileScene");
    } else {
      this.scene.start("launcher");
    }
  }

  private hex(n: number): string {
    return `#${n.toString(16).padStart(6, "0")}`;
  }

  /** Tiny pixel camera glyph for the "Take Photo" button — dark body with a
   *  cyan UGC lens glint. Drawn centred at (cx, cy) into the button graphics. */
  private static drawCameraIcon(g: Phaser.GameObjects.Graphics, cx: number, cy: number): void {
    g.fillStyle(PALETTE.win95Text, 1);
    g.fillRect(cx - 5, cy - 3, 10, 7); // body
    g.fillRect(cx + 1, cy - 5, 4, 2); // viewfinder hump
    g.fillStyle(PALETTE.win95LightGray, 1);
    g.fillRect(cx - 2, cy - 2, 5, 5); // lens ring
    g.fillStyle(UGC_CYAN, 1);
    g.fillRect(cx - 1, cy - 1, 3, 3); // lens glint (UGC cyan)
    g.fillStyle(PALETTE.win95HiLite, 1);
    g.fillRect(cx - 4, cy - 2, 1, 1); // flash highlight
  }
}
